/* eslint-disable consistent-return */
/* eslint-disable no-nested-ternary */
import React from 'react';
import moment from 'moment';
import { TableRow, Grid, TableCell, Typography } from '@material-ui/core';
import { startEndTimeDisplay } from '../../../utils/dates';
import { DaysOfWeek } from '../../../enums/daysOfWeek';
import recurrenceTypes from '../../../settings/constants/recurrenceTypes';
import globals from '../../../globals/communityGlobals';
import { MonthOption } from '../../../enums/monthOption';

const locale = globals.getArea('audit');
const daysOfWeekArray = Object.values(DaysOfWeek);
const backgroundColorTimeSlot = (selectedId, timeSlotsMatrix) => {
  return timeSlotsMatrix && timeSlotsMatrix[selectedId] ? 'selectedPatternSlot' : 'unselectedPatternSlot';
};

const isWeekly = recurrenceType => {
  return recurrenceType === recurrenceTypes.WEEKLY;
};
const isMonthly = recurrenceType => {
  return recurrenceType === recurrenceTypes.MONTHLY;
};

const formattedTimeNoSeconds = (time = '') => {
  if (!time) return;
  return moment(time, 'HH:mm:ss').format('HH:mm');
};

const hasSameTimeSlots = (oldTimeSlotsMatrix, newTimeSlotsMatrix) => {
  if (!oldTimeSlotsMatrix || !newTimeSlotsMatrix) {
    return false;
  }
  const oldTimeSlotsMatrixIds = Object.keys(oldTimeSlotsMatrix);
  const newTimeSlotsMatrixIds = Object.keys(newTimeSlotsMatrix);
  return (
    oldTimeSlotsMatrixIds.length === newTimeSlotsMatrixIds.length &&
    oldTimeSlotsMatrixIds.every(key => newTimeSlotsMatrixIds.includes(key))
  );
};

const renderMonthlyData = (monthlyRecurrenceType, selectedWeeks, selectedDays) => {
  return (
    <Grid item xs={12}>
      <p>
        Selected day/week calendar: <strong>{MonthOption[monthlyRecurrenceType]}</strong>
      </p>
      {MonthOption[monthlyRecurrenceType] === MonthOption.WeekOfTheMonth && (
        <p>
          Selected Week: <strong>{selectedWeeks}</strong>
        </p>
      )}
      <p>
        Selected Day: <strong>{selectedDays}</strong>
      </p>
    </Grid>
  );
};

function TreatmentTimeSlotsDataTable({
  timeSlotsPackage,
  timeSlotsMatrix,
  rowTitle,
  valueTitle,
  cssStyle,
  showInnerTitle,
  monthlyRecurrenceType,
  recurrenceType,
  selectedDays,
  selectedWeeks,
}) {
  return rowTitle && timeSlotsMatrix && timeSlotsPackage ? (
    <Grid
      container
      className={`timeSlots-dataTable ${cssStyle}`}
      key={`table_${Object.keys(timeSlotsMatrix).reduce((sum, key) => sum + Number(key), 0)}`}
    >
      {showInnerTitle && (
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            {rowTitle}
          </Typography>
        </Grid>
      )}
      <Grid item xs={12} justify="flex-start">
        <Typography variant="h4" gutterBottom>
          {valueTitle}
        </Typography>
      </Grid>
      {isMonthly(recurrenceType) && renderMonthlyData(monthlyRecurrenceType, selectedWeeks, selectedDays)}
      {timeSlotsPackage.timeSlots.map(slot => (
        <Grid item xs={2}>
          <span className="day">{startEndTimeDisplay(slot.from, slot.to)}</span>
          <div className="timeSlots-time">
            <div className={backgroundColorTimeSlot(slot.id, timeSlotsMatrix)}>{slot.name}</div>
          </div>
        </Grid>
      ))}
    </Grid>
  ) : (
    <Grid container className={`empty-timeSlots-dataTable ${cssStyle}`} />
  );
}

function SpecificTimeTreatmentDataTable({
  rowTitle,
  valueTitle,
  cssStyle,
  specificStartTime,
  selectedDays,
  monthlyRecurrenceType,
  selectedWeeks,
  recurrenceType,
}) {
  return specificStartTime ? (
    <Grid
      container
      className={`timeSlots-dataTable ${cssStyle}`}
      key={`table_${valueTitle + recurrenceType + monthlyRecurrenceType + selectedWeeks + selectedDays}`}
    >
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          {rowTitle}
        </Typography>
      </Grid>
      <Grid item xs={12} justify="flex-start">
        <Typography variant="h4" gutterBottom>
          {valueTitle}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        {specificStartTime && <p>Selected Start Time: {specificStartTime}</p>}
      </Grid>
      <Grid item xs={12}>
        {selectedDays && !isMonthly(recurrenceType) && <p>Selected Days: {selectedDays}</p>}
      </Grid>
      {isMonthly(recurrenceType) && renderMonthlyData(monthlyRecurrenceType, selectedWeeks, selectedDays)}
    </Grid>
  ) : (
    <Grid container className={`empty-timeSlots-dataTable ${cssStyle}`} />
  );
}

export default function SubRowTreatmentTimeSlotsDataTable({ timeSlotDetails }) {
  const isOldAndNewRecurrenceTypeWeekly =
    isWeekly(timeSlotDetails.oldRecurrenceType) && isWeekly(timeSlotDetails.newRecurrenceType);

  const getTreatmentsDayOfWeek = (treatmentDayOfWeek, day) => {
    if (isOldAndNewRecurrenceTypeWeekly) {
      return treatmentDayOfWeek.includes(day) ? daysOfWeekArray[day] : null;
    }

    return daysOfWeekArray[treatmentDayOfWeek[day]];
  };

  const renderTimeSlotsTable = (day, oldTreatmentDaysOfWeek, newTreatmentDaysOfWeek) => {
    const oldRecurrenceType = timeSlotDetails.oldRecurrenceType;
    const newRecurrenceType = timeSlotDetails.newRecurrenceType;

    const oldTimeSlotsMatrix =
      timeSlotDetails.oldTimeSlotsMatrix[isOldAndNewRecurrenceTypeWeekly ? day : oldTreatmentDaysOfWeek[day]];
    const newTimeSlotsMatrix =
      timeSlotDetails.newTimeSlotsMatrix[isOldAndNewRecurrenceTypeWeekly ? day : newTreatmentDaysOfWeek[day]];

    const oldTimeSlotsPackage = timeSlotDetails.oldTimeSlotsPackage;
    const newTimeSlotsPackage = timeSlotDetails.newTimeSlotsPackage;

    const oldTreatmentRecurrence = isWeekly(oldRecurrenceType)
      ? getTreatmentsDayOfWeek(oldTreatmentDaysOfWeek, day)
      : [oldRecurrenceType];
    const newTreatmentRecurrence = isWeekly(newRecurrenceType)
      ? getTreatmentsDayOfWeek(newTreatmentDaysOfWeek, day)
      : [newRecurrenceType];

    const rowTitle = oldTreatmentRecurrence || newTreatmentRecurrence;
    const hasNotChanged =
      oldRecurrenceType === newRecurrenceType &&
      hasSameTimeSlots(oldTimeSlotsMatrix, newTimeSlotsMatrix) &&
      oldTreatmentDaysOfWeek.includes(day) === newTreatmentDaysOfWeek.includes(day) &&
      timeSlotDetails.oldMonthlyRecurrenceType === timeSlotDetails.newMonthlyRecurrenceType &&
      timeSlotDetails.oldSelectedWeeks === timeSlotDetails.newSelectedWeeks &&
      (oldTimeSlotsPackage != null && oldTimeSlotsPackage.id) ===
        (newTimeSlotsPackage != null && newTimeSlotsPackage.id);
    if (hasNotChanged) {
      return (
        <Grid container className="dataTable-info-box" key={day}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {oldTreatmentRecurrence}
            </Typography>
          </Grid>
          <TreatmentTimeSlotsDataTable
            timeSlotsPackage={oldTimeSlotsPackage}
            timeSlotsMatrix={oldTimeSlotsMatrix}
            rowTitle={rowTitle}
            cssStyle={'bigTimeSlotDataTable'}
            showInnerTitle={false}
            monthlyRecurrenceType={timeSlotDetails.oldMonthlyRecurrenceType}
            recurrenceType={oldRecurrenceType}
            specificStartTime={formattedTimeNoSeconds(timeSlotDetails.oldSpecificStartTime)}
            selectedDays={timeSlotDetails.oldSelectedDays}
          />
        </Grid>
      );
    }

    return (
      <Grid container className="dataTable-info-box" key={day}>
        {isOldAndNewRecurrenceTypeWeekly && (
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {rowTitle}
            </Typography>
          </Grid>
        )}
        <TreatmentTimeSlotsDataTable
          timeSlotsPackage={oldTimeSlotsPackage}
          timeSlotsMatrix={!oldTimeSlotsMatrix && isWeekly(oldRecurrenceType) ? {} : oldTimeSlotsMatrix}
          rowTitle={rowTitle}
          valueTitle={locale.getMsg('originalValues')}
          cssStyle={'smallTimeSlotDataTable'}
          showInnerTitle={!isOldAndNewRecurrenceTypeWeekly}
          monthlyRecurrenceType={timeSlotDetails.oldMonthlyRecurrenceType}
          monthlyRecurrenceTypeDay={oldTreatmentDaysOfWeek[day]}
          selectedWeeks={timeSlotDetails.oldSelectedWeeks}
          recurrenceType={oldRecurrenceType}
          selectedDays={timeSlotDetails.oldSelectedDays}
        />
        <TreatmentTimeSlotsDataTable
          timeSlotsPackage={newTimeSlotsPackage}
          timeSlotsMatrix={!newTimeSlotsMatrix && isWeekly(newRecurrenceType) ? {} : newTimeSlotsMatrix}
          rowTitle={newTreatmentRecurrence || oldTreatmentRecurrence}
          valueTitle={locale.getMsg('newValues')}
          cssStyle={'smallTimeSlotDataTable'}
          showInnerTitle={!isOldAndNewRecurrenceTypeWeekly}
          monthlyRecurrenceType={timeSlotDetails.newMonthlyRecurrenceType}
          monthlyRecurrenceTypeDay={newTreatmentDaysOfWeek[day]}
          selectedWeeks={timeSlotDetails.newSelectedWeeks}
          recurrenceType={newRecurrenceType}
          selectedDays={timeSlotDetails.newSelectedDays}
        />
      </Grid>
    );
  };

  const renderSpecificTimeTable = () => {
    const oldRecurrenceType = timeSlotDetails.oldRecurrenceType;
    const newRecurrenceType = timeSlotDetails.newRecurrenceType;
    return (
      <Grid container className="dataTable-info-box">
        <SpecificTimeTreatmentDataTable
          rowTitle={oldRecurrenceType}
          valueTitle={locale.getMsg('originalValues')}
          cssStyle={'smallTimeSlotDataTable'}
          specificStartTime={formattedTimeNoSeconds(timeSlotDetails.oldSpecificStartTime)}
          selectedDays={timeSlotDetails.oldSelectedDays}
          monthlyRecurrenceType={timeSlotDetails.oldMonthlyRecurrenceType}
          selectedWeeks={timeSlotDetails.oldSelectedWeeks}
          recurrenceType={oldRecurrenceType}
        />
        <SpecificTimeTreatmentDataTable
          rowTitle={newRecurrenceType}
          valueTitle={locale.getMsg('newValues')}
          cssStyle={'smallTimeSlotDataTable'}
          timeSlotDetails={timeSlotDetails}
          specificStartTime={formattedTimeNoSeconds(timeSlotDetails.newSpecificStartTime)}
          selectedDays={timeSlotDetails.newSelectedDays}
          monthlyRecurrenceType={timeSlotDetails.newMonthlyRecurrenceType}
          selectedWeeks={timeSlotDetails.newSelectedWeeks}
          recurrenceType={newRecurrenceType}
        />
      </Grid>
    );
  };

  const renderTable = () => {
    if (timeSlotDetails.newSpecificStartTime) {
      return renderSpecificTimeTable();
    }
    const oldTreatmentDaysOfWeek = Object.keys(timeSlotDetails.oldTimeSlotsMatrix);
    const newTreatmentDaysOfWeek = Object.keys(timeSlotDetails.newTimeSlotsMatrix);

    const treatmentsSelectedDays = isOldAndNewRecurrenceTypeWeekly
      ? [...new Set([...oldTreatmentDaysOfWeek, ...newTreatmentDaysOfWeek])].sort((a, b) => a - b)
      : oldTreatmentDaysOfWeek.length > newTreatmentDaysOfWeek.length
      ? Array.from({ length: oldTreatmentDaysOfWeek.length }, (v, i) => i)
      : Array.from({ length: newTreatmentDaysOfWeek.length }, (v, i) => i);

    return treatmentsSelectedDays.map(day => renderTimeSlotsTable(day, oldTreatmentDaysOfWeek, newTreatmentDaysOfWeek));
  };

  return (
    <TableRow>
      <TableCell colSpan={8}>
        <Grid container className="subRow-TimeSlots-DataTable">
          {renderTable()}
        </Grid>
      </TableCell>
    </TableRow>
  );
}
